import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Download, FileText, Calendar, Clock, User, Award } from "lucide-react";
import { toast } from "react-toastify";

const GeneratedPapers = () => {
  const [papers, setPapers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Mock data for generated sample papers
  const mockPapers = [
    {
      id: 1,
      title: "Mathematics Sample Paper - Algebra",
      subject: "Mathematics",
      topic: "Algebra",
      questionCount: 25,
      difficulty: "Advanced",
      year: 2024,
      duration: "60 minutes",
      createdAt: "2024-01-15T10:30:00Z",
      timeSpent: "45 minutes",
      score: 88,
      status: "completed",
      type: "MCQ",
    },
    {
      id: 2,
      title: "Physics Sample Paper - Mechanics",
      subject: "Physics",
      topic: "Mechanics",
      questionCount: 30,
      difficulty: "Advanced",
      year: 2024,
      duration: "90 minutes",
      createdAt: "2024-01-14T14:20:00Z",
      timeSpent: "75 minutes",
      score: 92,
      status: "completed",
      type: "Mixed",
    },
    {
      id: 3,
      title: "Chemistry Sample Paper - Organic",
      subject: "Chemistry",
      topic: "Organic Chemistry",
      questionCount: 20,
      difficulty: "Intermediate",
      year: 2024,
      duration: "45 minutes",
      createdAt: "2024-01-13T09:15:00Z",
      timeSpent: "40 minutes",
      score: 85,
      status: "completed",
      type: "MCQ",
    },
    {
      id: 4,
      title: "Biology Sample Paper - Cell Biology",
      subject: "Biology",
      topic: "Cell Biology",
      questionCount: 22,
      difficulty: "Intermediate",
      year: 2024,
      duration: "50 minutes",
      createdAt: "2024-01-12T16:45:00Z",
      timeSpent: "48 minutes",
      score: 90,
      status: "completed",
      type: "True/False",
    },
    {
      id: 5,
      title: "Mathematics Sample Paper - Calculus",
      subject: "Mathematics",
      topic: "Calculus",
      questionCount: 18,
      difficulty: "Advanced",
      year: 2024,
      duration: "75 minutes",
      createdAt: "2024-01-11T11:30:00Z",
      timeSpent: "70 minutes",
      score: 95,
      status: "completed",
      type: "Short Answer",
    },
  ];

  useEffect(() => {
    // Simulate API call
    const timer = setTimeout(() => {
      setPapers(mockPapers);
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleDownload = (paper) => {
    // Create a dummy PDF download
    const link = document.createElement("a");
    link.href = "#";
    link.download = `${paper.title}.pdf`;

    // For now, we'll show a toast since we don't have actual PDFs
    toast.success(`Downloading ${paper.title}...`);

    // In a real implementation, you would:
    // 1. Make an API call to get the PDF
    // 2. Create a blob from the response
    // 3. Create a download link
  };

  const handleTakeQuiz = (paper) => {
    // Navigate to quiz with paper data
    navigate(`/practice/quiz?paperId=${paper.id}`);
    toast.info("Starting sample paper quiz...");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty.toLowerCase()) {
      case "beginner":
        return "bg-green-100 text-green-800";
      case "intermediate":
        return "bg-yellow-100 text-yellow-800";
      case "advanced":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return "text-green-600";
    if (score >= 80) return "text-blue-600";
    if (score >= 70) return "text-yellow-600";
    return "text-red-600";
  };

  const getTypeColor = (type) => {
    switch (type.toLowerCase()) {
      case "mcq":
        return "bg-blue-100 text-blue-800";
      case "mixed":
        return "bg-purple-100 text-purple-800";
      case "true/false":
        return "bg-orange-100 text-orange-800";
      case "short answer":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-white z-50 overflow-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-4">
          <button
            onClick={() => navigate("..")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="text-sm font-medium">Back to Sample Papers</span>
          </button>
        </div>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0D99FF]"></div>
            <p className="text-gray-600 font-medium">
              Loading sample papers...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-auto">
      {/* Header with back button */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-4">
        <button
          onClick={() => navigate("..")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span className="text-sm font-medium">Back to Sample Papers</span>
        </button>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row items-center sm:items-start mb-6 gap-4 sm:gap-6">
          <div className="bg-[#0D99FF] rounded-full p-3">
            <div className="bg-white rounded-full p-1">
              <div className="bg-[#0D99FF] rounded-full w-8 h-8 flex items-center justify-center">
                <span className="text-white text-xs sm:text-base">📋</span>
              </div>
            </div>
          </div>
          <div className="text-center sm:text-left">
            <h2 className="text-gray-700 text-lg font-medium">
              Generated Sample Papers
            </h2>
            <p className="text-gray-500 text-sm">
              View and download your previously generated sample papers
            </p>
          </div>
        </div>

        {/* Papers List */}
        <div className="space-y-4">
          {papers.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                No sample papers generated yet
              </h3>
              <p className="text-gray-500">
                Generate your first sample paper to see it here
              </p>
            </div>
          ) : (
            papers.map((paper) => (
              <div
                key={paper.id}
                className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {paper.title}
                      </h3>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(
                          paper.difficulty
                        )}`}
                      >
                        {paper.difficulty}
                      </span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(
                          paper.type
                        )}`}
                      >
                        {paper.type}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span>
                          {paper.subject} - {paper.topic}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        <span>{paper.questionCount} questions</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{paper.duration}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(paper.createdAt)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Award className="w-4 h-4" />
                        <span>Year {paper.year}</span>
                      </div>
                    </div>

                    {paper.status === "completed" && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Score:</span>
                        <span
                          className={`text-sm font-semibold ${getScoreColor(
                            paper.score
                          )}`}
                        >
                          {paper.score}%
                        </span>
                        <span className="text-sm text-gray-500">
                          ({paper.timeSpent})
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={() => handleTakeQuiz(paper)}
                      className="px-4 py-2 bg-[#0D99FF] text-white rounded-lg hover:bg-[#0D99FF]/90 transition-colors text-sm font-medium"
                    >
                      Take Quiz
                    </button>
                    <button
                      onClick={() => handleDownload(paper)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download PDF
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default GeneratedPapers;
